// Events controller — extracted verbatim from GameScene.ts (FACE&FURY S2 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were
// rewritten to `export function name(this: GameScene, ...)` and bound back onto the scene as fields.
import Phaser from 'phaser';
import { type Snapshot, SCORING } from '../../core';
import { PALETTE, hex, FONT } from '../keys';
import { shake, flash, particleBurst, floatText, clearActiveFloats, glowPulse, hitstop, squashStretch, vignettePulse, shockwaveRing, celebrate, cameraPunch, heroWordmark, styleHeroText, mult } from '../juice';
import { audio } from '../audio';
import { haptic, motionAllowed } from '../settings';
import { isLandscape, LANDSCAPE_GUTTER_W } from '../stage';
import { typePx } from '../ui/typeScale';
import { recordFeelReviewEvents } from '../playtestSession';
import { track, trackOnce } from '../../analytics/client';
import { SURGE_CORE, SURGE_EDGE, PLAYER, AI } from './types';
import { buildGhostTrack } from '../ghostTrack';
import type { GameScene } from '../scenes/GameScene';

// --- FACE & FURY S5 CRESCENDO tuning (renderer-only presentation constants) ----------------------
/**
 * PHOTO-FINISH margin (points). When the board-deciding commit lands and the human's final score is
 * within this of the rival's (or the ghost's, in a duel), the transition gets the bullet-time beat.
 * 150 ≈ a single un-combo'd correct answer (+100) plus change — a finish this tight is a genuine
 * nail-biter, not a routine win/loss. Renderer-derived from the final scores; never a Core change.
 */
export const PHOTO_FINISH_MARGIN = 150;
/**
 * Board-end → Result handoff windows (wall-clock ms) for the two board-end set-pieces. The normal
 * clear stays on the existing 420ms contract; a set-piece deliberately holds so its payoff reads
 * before the scene swaps. Kept under the result-handoff regression budget (1800ms) so the "hitstop
 * must not stretch the handoff" guard still passes for a normal board.
 */
export const PERFECT_GAUNTLET_HANDOFF_MS = 1750;
export const PHOTO_FINISH_HANDOFF_MS = 1150;


  // --- juice: music intensity + near-miss tension --------------------------
  /**
   * Drive the tension music layer (and the near-miss vignette) from the moment's heat:
   *   - low timer (last ~30%) ramps intensity,
   *   - a hazard within 1-2 cells of the human spikes it AND fires the near-miss cue + SFX,
   *   - a hot combo adds a little on top.
   * We only push setIntensity when it moves meaningfully so the bus isn't churned every frame.
   */
export function updateMusicIntensity(this: GameScene, snap: Snapshot): void {
    const human = snap.players.find((p) => p.id === PLAYER);
    if (!human) return;
    // While PROTECTED (safe ground or mid-surge) a nearby hazard cannot hurt the human, so we suppress
    // the "danger" warnings — crying wolf about a threat you're immune to erodes the signal (owner slice).
    const protectedNow = this.isHumanProtected(snap, human);

    const timeFrac = Phaser.Math.Clamp(snap.timeLeftMs / this.timerMaxMs, 0, 1);
    const timerHeat = timeFrac < 0.3 ? (0.3 - timeFrac) / 0.3 : 0; // 0..1 over the last 30%

    // Manhattan distance from the human to the nearest hazard, in cells.
    let nearest = Infinity;
    const hr = Math.floor(human.pos / this.cols);
    const hc = human.pos % this.cols;
    // Pre-step TELEGRAPH lookahead: when a NEARBY hazard is within ~220ms of its next step, flash a
    // warning on the cell it currently occupies so an imminent lunge is signalled a beat before it
    // lands. Throttled per-hazard by its nextStepMs (so it fires at most once per upcoming step) and
    // gated to hazards already close to the human (cheap; no telegraph spam across the whole board).
    const STEP_LOOKAHEAD = 220;
    for (const hz of snap.hazards) {
      const zr = Math.floor(hz.pos / this.cols);
      const zc = hz.pos % this.cols;
      const d = Math.abs(zr - hr) + Math.abs(zc - hc);
      nearest = Math.min(nearest, d);
      if (
        d <= 2 &&
        human.alive &&
        !protectedNow &&
        hz.pausedUntilMs <= snap.timeMs &&
        hz.nextStepMs - snap.timeMs <= STEP_LOOKAHEAD &&
        hz.nextStepMs - snap.timeMs > 0 &&
        this.hazardTelegraphAt.get(hz.id) !== hz.nextStepMs
      ) {
        this.hazardTelegraphAt.set(hz.id, hz.nextStepMs);
        this.hazardTelegraph(hz.pos, d <= 1);
      }
    }
    const hazardHeat = nearest <= 1 ? 1 : nearest === 2 ? 0.6 : nearest === 3 ? 0.25 : 0;
    const comboHeat = Phaser.Math.Clamp((human.combo - 2) / 6, 0, 0.5);

    let intensity = Phaser.Math.Clamp(Math.max(timerHeat, hazardHeat) + comboHeat * 0.4, 0, 1);
    // SIGNATURE PEAK: a crescendo slams musicSwell to 1; floor the bed with it so the music JUMPS to
    // full on the chain, then settles back as the swell decays. While a swell is active we bypass the
    // 0.08 delta throttle so the slam (and the smooth settle each frame) is heard, not quantised away.
    const swelling = this.musicSwell > 0.01;
    if (swelling) intensity = Math.max(intensity, this.musicSwell);
    if (swelling || Math.abs(intensity - this.lastIntensity) > 0.08) {
      this.lastIntensity = intensity;
      audio().setIntensity(intensity);
    }

    // Near-miss: hazard adjacent + alive + NOT protected -> a brief red edge vignette + a warning blip.
    if (nearest <= 1 && human.alive && !protectedNow && human.stunnedUntilMs <= snap.timeMs) {
      if (this.time.now >= this.nextNearMissMs) {
        this.nextNearMissMs = this.time.now + 900;
        vignettePulse(this, PALETTE.hazard, 420, 0.32);
        audio().playSfx('hazardNear', { volume: 0.55 }); // ambient warning yields to action feedback (mix-clutter review)
        haptic(25);
      }
    }
  }


  // --- juice: consume new events from the sim log --------------------------
export function consumeEvents(this: GameScene, snap: Snapshot): void {
    const fresh = this.sim.log.since(this.lastEventTime);
    if (fresh.length === 0) return;
    this.lastEventTime = snap.timeMs;
    recordFeelReviewEvents(this.registry, fresh, this.seed);

    // L5: a "threaded" eat fires munchCorrect + gauntlet on the SAME cell+time. Collect those cells
    // so we float ONE combined "THREADED +N" instead of two overlapping labels.
    const gauntletCells = new Set<number>();
    for (const e of fresh) {
      if (e.type === 'gauntlet' && e.player === PLAYER && e.cell !== undefined) gauntletCells.add(e.cell);
    }

    // S5R fix (audio triple-stack): if this SAME drained batch also ENDS the board, the board-clear /
    // PERFECT-GAUNTLET set-piece is about to own the audio moment. A tier-3 chain finish would otherwise
    // stack chainRiser3 + voGauntletChain UNDER the set-piece sting (three cues ~1 frame apart). Peek the
    // batch once and suppress the final munch's chain riser + VO (its visual ×N stamp still fires).
    // Renderer-only, no state kept past this call.
    const boardEndsThisBatch = fresh.some((ev) => ev.type === 'boardWin' || ev.type === 'boardLose');

    for (const e of fresh) {
      // Any rival-attributed event retires the board-start "thinking" tell on its readout.
      if (e.player === AI) this.rivalHasActed = true;
      const at = e.cell !== undefined && e.cell >= 0 ? this.cellCenter(e.cell) : null;
      switch (e.type) {
        case 'munchCorrect': {
          if (at) {
            const threaded = e.cell !== undefined && gauntletCells.has(e.cell);
            particleBurst(this, at.x, at.y, threaded ? PALETTE.armed : PALETTE.correct, threaded ? 16 : 12);
            // art VFX pop on top of the particle spray (additive; no-op without art).
            this.playVfx(threaded ? 'vfxComboBurst' : 'vfxMunch', at.x, at.y, this.cellW * 1.1);
            if (e.player === PLAYER) {
              trackOnce('first_correct', 'first_correct', this.analyticsDims(snap, { result: 'correct' }));
              // #34: on the UNTIMED, hazard-free first training board, the human's very first correct CHOMP
              // earns a one-shot "FIRST BITE!" hero float — the missing positive payoff for a brand-new
              // player's opening act (mirrors the timer/hearts being hidden there: pure encouragement, no
              // fail pressure). Gated to novice board 0 + once; heroWordmark cut, motion-gated inside.
              if (this.noviceRun && this.boardIndex === 0 && !this.firstBiteShown) {
                this.firstBiteShown = true;
                this.showFirstBiteStamp();
              }
              // #32 SINGLE-THREAD DEFLATION (VFX weight only — the gauntletArmThreshold 2→1 lever is
              // LOCKED, decision 1a): at armThreshold=1 a single thread fires on nearly every correct, so
              // the beefed board-wide "orbital" shockwave + the harder shake are RESERVED for chain≥2
              // (bigRing); a single thread rings/shakes at the ordinary-correct weight (still amber-tinted
              // so it keeps its threaded identity). chainStreak is pre-increment here, so this eat reaches
              // chain (chainStreak + 1) — bigRing ⇒ this eat reaches chain ≥ 2.
              const bigRing = threaded && this.chainStreak >= 1;
              shake(this.cameras.main, bigRing ? 4.5 : 3, bigRing ? 130 : 90);
              shockwaveRing(this, at.x, at.y, threaded ? PALETTE.armed : PALETTE.correct, {
                maxRadius: this.cellW * (bigRing ? 1.9 : 1.35),
                durationMs: bigRing ? 390 : 300,
                thickness: Math.max(3, this.cellW * 0.075),
                peakAlpha: bigRing ? 0.58 : 0.42,
              });
              // SIGNATURE PEAK re-gate (S5): the crescendo now keys on the GAUNTLET CHAIN — CONSECUTIVE
              // threaded corrects with no decoy eaten between them (chainStreak), not the raw per-board
              // thread count (which at armThreshold=1 is now every board — decision 1a). `chainStreak` is
              // still pre-increment here, so the chain THIS eat reaches is `+ 1`. Only the FULL crescendo
              // (chain ≥ 4) runs its own longer slow-mo, so ONLY it skips the per-munch freeze; chain 2/3
              // keep the crunchy per-munch hitstop under their (lighter) tier beat.
              const upcomingChain = threaded ? this.chainStreak + 1 : 0;
              const chainEat = upcomingChain >= 4;
              // HITSTOP — the classic munch freeze; threaded eats hit a touch harder. On the full-crescendo
              // eat we SKIP it: fireGauntletChainCrescendo runs its own longer hitstop, and a same-frame
              // 80ms restore-timer would otherwise cut that peak short.
              if (!chainEat) hitstop(this, threaded ? 105 : 82);
              // squash/stretch the muncher for chunky weight + a haptic tick. Amounts raised
              // (0.3/0.4 -> 0.38/0.5) with the rest of the feel-amplitude pass: the chomp deform
              // has to survive 540p/25fps capture to read as a bite.
              squashStretch(this, this.humanSprite, { amount: threaded ? 0.5 : 0.38 });
              // VG-8: BITE RECOIL — a tight, non-collapsing scale-pop on the muncher BODY itself so each
              // eat lands with extra physical impact on top of the directional squash-stretch above. It
              // pops a child (the art image) so it layers cleanly with the container squash (separate
              // target) and never fights it; threaded eats pop a touch harder (mirrors the distinction).
              this.muncherBitePop(this.humanSprite, threaded);
              // chomp frame (art only) — bites down then returns to idle.
              this.pulseSpriteFrame(this.humanSprite, 'playerChomp', 'playerIdle', threaded ? 220 : 150);
              // Animated raster muncher (when its sheet decoded): play the chomp reaction over the idle.
              this.playMuncherAnim(this.humanSprite, 'mun-player-chomp');
              haptic(threaded ? [20, 30, 40] : 18);
              this.pulseDockButton(this.munchButton, threaded ? PALETTE.armed : PALETTE.correct, threaded ? 560 : 420);
              // J1 — the genre's #1 first-30s hook (Pac-Man 256 "wakka"): each consecutive correct munch
              // climbs the pitch a clear STEP so a run audibly "climbs"; it resets when the combo breaks
              // on a decoy. More pronounced than a subtle drift so the escalation is felt, not guessed.
              const human = snap.players.find((p) => p.id === PLAYER);
              const combo = human?.combo ?? 0;
              // Cap the climb at +40% — the first-listen review found the top combo steps piercing.
              // ±3% humanizing jitter (renderer-only, like moveStep's) so long runs don't fatigue
              // (post-fix re-listen suggestion #2). Never feeds back into sim state.
              const rate = 1 + Phaser.Math.Clamp(combo * 0.05, 0, 0.4) + (Math.random() - 0.5) * 0.06;
              audio().playSfx(threaded ? 'gauntlet' : combo >= 3 ? 'munchHi' : 'munch', { rate });
              if (threaded) {
                // threadsThisBoard = TOTAL threads this board (drives Flawless Feast + the Result stat);
                // chainStreak = CONSECUTIVE threads (the crescendo lever, reset on a decoy-eat above).
                this.threadsThisBoard += 1;
                this.chainStreak += 1;
                // BYT3 gets rattled by a thread: panic emote now, a delayed snark after the
                // announcer's line (playBark yields to VO on its own).
                this.rivalPersona?.reactPlayerThreaded();
                if (this.chainStreak >= 2) {
                  // 2nd+ CONSECUTIVE thread → the escalating GAUNTLET CHAIN crescendo (tier scales with the
                  // chain: 2→compact, 3→bigger+zoom+music, 4+→full screen-level peak). It OWNS this munch's
                  // set-piece SFX/VO (voThreaded suppressed) — see fireGauntletChainCrescendo.
                  this.fireGauntletChainCrescendo(at, this.chainStreak, e.value, e.t, boardEndsThisBatch);
                } else {
                  // A single ordinary thread (chain 1): the existing THREADED IT beat only — no crescendo.
                  // #32 SINGLE-THREAD DEFLATION: at armThreshold=1 this fires on nearly every correct, so it
                  // must stay a LOCAL amber pop — the lens punch + full-screen edge vignette are DROPPED
                  // (reserved for the chain≥2 crescendo, which owns escalation). The identity read is fully
                  // preserved: the amber THREADED IT float, a local particle burst, and the voThreaded
                  // callout. VFX weight only — the gauntletArmThreshold 2→1 lever is untouched (locked 1a).
                  particleBurst(this, at.x, at.y, PALETTE.armed, 12);
                  // Keep the single-thread payoff in the command-hint/toast lane instead of over answer
                  // cells; still review caught the old board float smearing through scan-state labels.
                  // Reward outranks the cosmetic SKIPPED pill: free the one toast slot so a first-time
                  // pass-through -> threaded-CHOMP sequence cannot hide the payoff for ~720ms.
                  this.dismissSafePassPill();
                  this.showCommandHint(`THREADED IT ${mult('1.5')}  +${e.value ?? ''}`, PALETTE.armed, 1800);
                  audio().playVo('voThreaded');
                }
              } else {
                // Keep active answer labels clear: the tile already carries the local chomp through
                // particles, bloom, sprite recoil, audio, and the SCORE readout pulse in updateHud().
                // A second floating +N repeatedly fought either live answer labels or persistent HUD
                // chips in narrow viewports, so routine positive ticks stay on the readout itself.
              }
            } else if (e.player === AI && this.aiEnabled) {
              this.showRivalMunchBeat(at, true, e.value, e.cell);
              this.rivalPersona?.reactRivalCorrect();
            }
            const v = e.cell !== undefined ? this.cellViews[e.cell] : undefined;
            if (v && v.container.active) {
              // Crunchy correct-munch on the TILE: a quick non-collapsing scale pop that returns to
              // base (so the freshly-revealed green ✓ stays on screen + legible), a glow ring, a bright
              // bloom that "empties" the tile into its eaten state, and a crumb burst. A threaded eat
              // recolors to amber and hits harder (brighter bloom + an extra reward glow on top).
              this.crunchCorrectCell(v, at, e.player === PLAYER, threaded);
              if (threaded) glowPulse(this, v.container, v.w, v.h, PALETTE.armed, 600);
            }
          }
          break;
        }
        case 'munchDecoy': {
          if (e.player === PLAYER) {
            // SIGNATURE PEAK reset: eating a decoy BREAKS the GAUNTLET CHAIN (the non-farmable measure —
            // decision 1a). A C→D→C ping-pong can't farm the crescendo because the moment you CHOMP the
            // decoy your consecutive-thread streak drops to 0 and you must rebuild it clean.
            this.chainStreak = 0;
            trackOnce('first_wrong', 'first_wrong', this.analyticsDims(snap, { result: 'wrong' }));
            // Keep the miss cue readable: the tile, wrong-buzz VFX, haptic, score flash, and edge frame
            // carry the failure. A strong full-screen red wash made answer labels unreadable in stills.
            flash(this, PALETTE.hazard, 130, 0.04);
            vignettePulse(this, PALETTE.hazard, 240, 0.12);
            shake(this.cameras.main, 6, 140);
            audio().playSfx('wrong');
            haptic([40, 30, 40]);
            this.pulseDockButton(this.munchButton, PALETTE.hazard, 420);
            // Animated raster muncher (when present): hurt flinch (tint+shudder) on a decoy chomp.
            this.reactMuncher(this.humanSprite, 'hurt');
            if (at) {
              this.playVfx('vfxWrongBuzz', at.x, at.y, this.cellW * 1.1);
              // The point-loss float lives in the score/HUD lane, not over a live answer label. The
              // tile-local X + wrong-buzz VFX carry the spatial "bad chomp"; this callout carries cost.
              const lossX = Phaser.Math.Clamp(this.gridX + this.cellW * 0.5, 58, this.scale.width * 0.24);
              const lossY = Math.max(118, this.gridY - Math.max(92, this.cellH * 1.2));
              floatText(this, lossX, lossY, `DECOY −${Math.abs(e.value ?? -60)}`, hex(PALETTE.white), 21);
            }
            const human = snap.players.find((p) => p.id === PLAYER);
            if (this.scanUnlocked && !this.taughtWrongScan && (human?.scansLeft ?? 0) > 0) {
              this.taughtWrongScan = true;
              this.showTeachBeat('Use SCAN before a risky guess');
              // S3 item 10: halo the button (real w/h + margin) so the teach pulse rings AROUND SCAN,
              // not as an inner "doubled border".
              const bw = (typeof this.scanButton.getData('w') === 'number' ? (this.scanButton.getData('w') as number) : Math.min(this.scale.width * 0.3, 130)) + 12;
              const bh = (typeof this.scanButton.getData('h') === 'number' ? (this.scanButton.getData('h') as number) : 46) + 12;
              glowPulse(this, this.scanButton, bw, bh, PALETTE.scan, 720);
            }
          } else if (at) {
            if (this.aiEnabled) this.showRivalMunchBeat(at, false, e.value, e.cell);
            this.rivalPersona?.reactRivalDecoy(); // its signature fallibility moment — always voiced
            particleBurst(this, at.x, at.y, PALETTE.hazard, 6);
          }
          break;
        }
        case 'passArm': {
          // J2 — moving through a decoy is the signature Gauntlet route; juice it as a rewarding near-miss
          // with a distinct cyan "whoosh" and SKIPPED banner, clearly different from CHOMP juice. This
          // teaches the rule: moving away is free, only CHOMPing a decoy costs. The decoy still locks behind you.
          if (e.player === PLAYER) {
            // J5 — the skip-through is a satisfying near-miss, not a lock thunk. Give it a
            // dedicated smooth "whoosh" (pitched-up scan) so going THROUGH sounds GOOD and is audibly
            // distinct from the heavier lock SFX used elsewhere. The decoy still visibly arms behind you.
            audio().playSfx('scan', { rate: 1.3, volume: 0.5 });
            haptic(15);
            if (at) {
              particleBurst(this, at.x, at.y, PALETTE.scan, 8);
              const v = e.cell !== undefined ? this.cellViews[e.cell] : undefined;
              // A pass-THROUGH does not eat the cell, so its country label stays on screen. Keep the
              // localized particles/glow on the tile.
              // TEACH BEFORE CELEBRATE (fix 4): on the FIRST armed pass-through of each board, the LOCKED
              // teach line OWNS the ONE toast slot — the SKIPPED pill would hide commandHintText and
              // swallow the teach text (the load-bearing 'skipping is FREE. Only CHOMP costs.' cue).
              // Every LATER pass this board shows the SKIPPED pill as before. Copy names the padlock the
              // player just watched appear — truth-neutral, amber to tie the words to the amber padlock.
              if (!this.taughtArm) {
                this.taughtArm = true;
                this.showCommandHint('ROUTE AROUND LOCKS — CORRECT CHOMP ×1.5', PALETTE.armed, 4500);
                // #37: on the novice FENCED board (index 1, fenceRatio 0.45 with a guaranteed fenced
                // correct — see noviceBoardSeed), the first ARMED traversal IS the threading lesson. Fire a
                // one-shot coach beat that names the payoff so the player connects "route THROUGH a decoy"
                // → "CHOMP a correct = THREADED IT ×1.5" on the mandatory path (not just in the optional
                // pre-round card). Above-grid teach card (distinct zone from the LOCKED toast); short delay
                // so the two beats read in sequence, not stacked. Fires on the ARM, never on an eat.
                if (this.noviceRun && this.boardIndex === 1 && !this.taughtNoviceThread) {
                  this.taughtNoviceThread = true;
                  this.time.delayedCall(380, () => {
                    if (!this.scene.isActive() || this.ended || this.paused || this.preRoundActive) return;
                    this.showTeachBeat('Threaded it! Now CHOMP a correct answer for a ×1.5 GAUNTLET bonus.');
                  });
                }
              } else {
                // The standing SKIPPED×N pill in the above-grid status band, so it can never cover active
                // answer labels during dense gauntlet routing.
                this.showSafePassBanner();
              }
              if (v && v.container.active) glowPulse(this, v.container, v.w, v.h, PALETTE.scan, 360);
            }
          }
          break;
        }
        case 'blocked': {
          if (e.player === PLAYER) {
            const msg =
              e.value === 0
                ? 'No scans left. Move or CHOMP the tile under you.'
                : 'Blocked. Tap an open tile to walk there.';
            this.showCommandHint(msg, PALETTE.armed);
            audio().playSfx('uiTap', { volume: 0.45 });
            haptic(8);
          }
          break;
        }
        case 'scan': {
          // Item 3a juice: pulse a glow on the cell-under + its orthogonal neighbors that just got
          // revealed (matches applyScan's reveal set). True status is drawn by renderCell.
          if (e.player === PLAYER && e.cell !== undefined) {
            track('scan_used', this.analyticsDims(snap));
            audio().playSfx('scan');
            haptic(12);
            this.pulseDockButton(this.scanButton, PALETTE.scan, 460);
            this.showCommandHint('SCAN REVEALED TILE + NEIGHBORS', PALETTE.scan);
            // Keep scan feedback per-cell. The wide raster ripple looked good in motion, but still
            // audits caught it crossing live answer labels on desktop; the reveal frames + cell glows
            // preserve the scan beat without covering text.
            this.pulseScanReveal(snap, e.cell);
          }
          break;
        }
        case 'combo': {
          if (e.player === PLAYER) {
            const c = e.value ?? 0;
            // J5 — the combo readout was static text. Make it FEEL like it's climbing: a brief scale
            // pop + a tier recolor (white -> green -> amber -> hot) each time the combo ticks up, so the
            // multiplier escalation reads on-screen, not just in the score. Motion-gated.
            this.pulseComboReadout(c);
            this.showComboTierMilestone(c);
            if (c >= 3) {
              const intensity = Phaser.Math.Clamp(3 + c, 4, 10);
              shake(this.cameras.main, intensity, 120);
            }
            // Tiered combo audio: rising blips at 3/5, a topper at 7, plus announcer milestones.
            if (c === 3) audio().playSfx('combo1');
            else if (c === 5) {
              audio().playSfx('combo2');
              audio().playVo('voCombo');
            } else if (c === 7) {
              audio().playSfx('combo3');
              audio().playVo('voSuperCombo');
            } else if (c >= 10 && c % 3 === 1) {
              audio().playSfx('combo3', { rate: 1.1 });
              audio().playVo('voUnstoppable');
            }
          }
          break;
        }
        case 'gauntlet': {
          if (e.player === PLAYER) track('route_bonus_triggered', this.analyticsDims(snap, { result: 'threaded' }));
          // L5: float + SFX handled by the combined munchCorrect branch above — do NOT double-fire.
          break;
        }
        case 'hazardSpawn': {
          // TELEGRAPH a fresh hazard's arrival on its spawn cell so it never appears ON the player
          // un-warned. Derived from the event's spawn cell only (no Core change). Mark the cell as
          // recently telegraphed so the per-step proximity warning doesn't double-fire this same tick.
          if (e.cell !== undefined && e.cell >= 0) {
            this.hazardTelegraph(e.cell, true);
            if (e.hazard !== undefined) this.hazardTelegraphAt.set(e.hazard, snap.timeMs);
          }
          break;
        }
        case 'hazardHit': {
          if (e.player === PLAYER) {
            flash(this, PALETTE.hazard, 220, 0.5);
            shake(this.cameras.main, 8, 160);
            cameraPunch(this, 0.05, 200);
            hitstop(this, 70);
            this.pulseSpriteFrame(this.humanSprite, 'playerHurt', 'playerIdle', 420);
            this.reactMuncher(this.humanSprite, 'hurt');
            haptic([50, 40, 80]);
            // life-lost SFX is fired by the lives edge-detect in renderSnapshot so it also covers
            // non-hazard life loss; here we just punch the hit itself.
          }
          break;
        }
        case 'hazardPurged': {
          // CHOMP SURGE payoff — the muncher DESTROYS a hazard on contact. The purged hazard's sprite is
          // already gone (renderSnapshot dropped it this frame), so anchor the pop at the purger's cell.
          const purger = snap.players.find((p) => p.id === e.player);
          const cell = purger ? this.cellCenter(purger.pos) : null;
          if (cell) {
            const reduced = !motionAllowed();
            particleBurst(this, cell.x, cell.y, SURGE_EDGE, reduced ? 8 : 16); // white-hot electric spray
            if (e.player === PLAYER) {
              if (!reduced) {
                shockwaveRing(this, cell.x, cell.y, SURGE_CORE, { maxRadius: 90, durationMs: 340 });
                cameraPunch(this, 0.03, 160);
                this.muncherBitePop(this.humanSprite, false);
              }
              floatText(this, cell.x, cell.y, `+${e.value ?? SCORING.surgePurgeBonus} PURGED!`, hex(SURGE_EDGE), 20, { clearance: 'high' });
              audio().playSfx('munchHi', { rate: 0.9 }); // a heavy bite — the muncher ate the hazard
              haptic([30, 30, 30]);
            }
          }
          break;
        }
        case 'boardWin':
        case 'boardLose': {
          const won = e.type === 'boardWin';
          // Wipe any in-flight score floats so the BOARD CLEAR hero banner reads ALONE (the video gate
          // flagged trailing MUNCH/THREADED floats cluttering the clear moment).
          clearActiveFloats(this);
          // S9 FIX-D (G1 AMATEUR 2, residual): the WINNING munch's own ×N stamp is suppressed in
          // landscape (fireGauntletChainCrescendo), but a PRIOR munch's stamp can still be mid-hold /
          // mid-fade when the clear banner lands — same board-centre anchor, same superimposition
          // (fresh capture caught a fading ×5 under CLINCHED!). Tear the live stamp down with the
          // floats so the board-end banner reads ALONE. Landscape only (portrait's above-grid stamp
          // band and the centred banner are disjoint); the perfect-gauntlet set-piece already clears
          // the whole spectacle via clearChainSpectacle.
          if (isLandscape() && this.chainStampObj) {
            this.tweens.killTweensOf(this.chainStampObj);
            this.chainSpectacleObjs.delete(this.chainStampObj);
            this.chainStampObj.destroy();
            this.chainStampObj = undefined;
          }
          // The shimmer, stamp, confetti, and Result handoff carry board wins; keep full-screen washes for
          // loss/hazard only so reward feedback never obscures the board.
          if (!won) flash(this, PALETTE.hazard, 240, 0.4);
          // muncher reaction frame (art only): triumphant on win, hurt on loss — held to results.
          this.setSpriteFrame(this.humanSprite, won ? 'playerWin' : 'playerHurt');
          this.reactMuncher(this.humanSprite, won ? 'win' : 'hurt');
          if (this.aiEnabled) this.rivalPersona?.reactBoardEnd(won);
          // S6: latch the board-token pose to the outcome — DEFEAT when the rival lost this board
          // (human won), TAUNT when the rival took it. Holds until the board tears down for Result.
          if (this.aiEnabled) this.setRivalPose(won ? 'defeat' : 'taunt');
          const winHuman = won ? snap.players.find((p) => p.id === PLAYER) : undefined;
          // PERFECT GAUNTLET (S5 — the signature clip): a STRICT superset of Flawless Feast. Won, zero
          // decoys eaten, ≥3 corrects, AND every single correct was threaded. When it fires it OWNS the
          // clear entirely — the flawless/clear banners + their VO/SFX are all suppressed so the
          // screen-consuming set-piece (fired here) + its sting are the ONE hero of the moment. The
          // longer Result handoff is picked up in tickFrame off this flag.
          const perfectGauntlet = won && this.detectPerfectGauntlet(snap);
          if (perfectGauntlet) this.perfectGauntlet = true;
          // PERFECT FEAST: a FLAWLESS gauntlet board — zero decoys eaten AND at least one thread this
          // board. This is the celebratory peak board-clear; it OWNS the win VO/SFX + banner (see below)
          // so we don't stack two announcer lines or two hero banners over the clear.
          const flawlessFeast =
            !perfectGauntlet && won && winHuman !== undefined && winHuman.eatenDecoy === 0 && this.threadsThisBoard >= 1;
          if (won) {
            if (perfectGauntlet) {
              // The screen-consuming set-piece owns the clear — no board-clear shimmer/SFX/VO/banner here.
              this.firePerfectGauntletSetPiece();
            } else {
              // board-clear shimmer over the whole grid (art only).
              const bcx = this.gridX + this.boardW / 2;
              const bcy = this.gridY + (this.cellH * this.rows) / 2;
              this.playVfx('vfxBoardClear', bcx, bcy, this.boardW * 0.9, 600);
              audio().playSfx('boardClear');
              if (flawlessFeast) {
                // A distinct, MORE celebratory clear: gold confetti + FLAWLESS hero stamp + its own VO.
                // The flawless VO/SFX REPLACE the perfect-board line so we never stack two announcer cues.
                audio().playSfx('perfect');
                audio().playVo('voFlawless');
                // S6 occlusion fix: the confetti shower renders BEHIND the board panel / RULE banner /
                // readouts / tile labels (depth 0.9 — above the world+backdrop, below all board+HUD
                // content, the S3 confetti-5 discipline). The FLAWLESS hero stamp (12_500) stays the
                // focal beat over the board; the shower is ambient sparkle in the margins. Zero frames
                // where celebration hides the RULE line or an unrevealed tile label.
                celebrate(this, [0xffd54a, PALETTE.armed, PALETTE.correct, PALETTE.white], { count: 90, depth: 0.9 });
                this.showFlawlessFeast();
              } else {
                // PERFECT board (no decoys eaten, but no thread) gets the bigger sting + announcer line.
                if (winHuman && winHuman.eatenDecoy === 0) {
                  audio().playSfx('perfect');
                  audio().playVo('voPerfectBoard');
                } else {
                  audio().playVo('voDelicious');
                }
              }
              shake(this.cameras.main, flawlessFeast ? 7 : 5, 220);
              cameraPunch(this, flawlessFeast ? 0.055 : 0.04, 220);
              haptic([30, 50, 30, 50]);
            }
          } else {
            audio().playSfx('lifeLost', { volume: 0.9 });
          }
          // Crossfade music down toward the results screen. Clear any active swell so it can't fight
          // the crossfade (the flawless/perfect-gauntlet stamps are their own beat, not a music swell).
          this.musicSwell = 0;
          audio().setIntensity(0);
          // The FLAWLESS / PERFECT-GAUNTLET stamps replace the normal "BOARD CLEAR" banner so the clear
          // reads with a single hero line. Only draw the standard banner when NEITHER owns the moment.
          if (!flawlessFeast && !perfectGauntlet) {
            const W = this.scale.width;
            const H = this.scale.height;
            // VG-7: the board-clear banner read "placeholder" next to the branded floats — give it the
            // display font + stroke + drop-shadow and a motion-gated pop so it matches the polished UI.
            // Payoff that lands the buildup arc: in rival mode the win/loss IS beating the rival, so say
            // so ("CLINCHED!" / "RIVAL TOOK IT") rather than the neutral solo "BOARD CLEAR / BOARD OVER".
            const bannerText = won
              ? this.aiEnabled
                ? 'CLINCHED!'
                : 'BOARD CLEAR'
              : this.aiEnabled
                ? 'RIVAL TOOK IT'
                : 'BOARD OVER';
            const banner = this.add
              .text(W / 2, H / 2, bannerText, {
                fontFamily: FONT.display,
                fontSize: `${typePx(this, 'display', { vw: 0.066, min: 30, max: 52 })}px`,
                color: hex(won ? PALETTE.correct : PALETTE.hazard),
                fontStyle: 'bold',
                stroke: '#0a0b12',
                strokeThickness: 6,
              })
              .setOrigin(0.5)
              .setDepth(12_000);
            banner.setShadow(0, 3, '#000000', 8, false, true);
            // #12: a WON clear (CLINCHED / BOARD CLEAR) wears the shared gold→amber hero cut; a LOSS
            // stays hazard-red (the gold treatment is a victory-only read).
            if (won) {
              styleHeroText(banner, { strokeThickness: 6 });
              // #5: CLINCHED read MUDDY on a darkened board — add a soft additive GOLD halo behind the
              // wordmark (on top of the #53 brighter metallic gold in styleHeroText) so the win banner
              // always lifts off the veil. Static + low-alpha ⇒ survives reduceMotion, never a strobe.
              // Sits just behind the banner; scene-owned + tracked for SHUTDOWN teardown.
              const halo = this.add
                .ellipse(W / 2, H / 2, banner.width * 1.2 + 60, banner.height * 1.6 + 24, 0xffd76a, 0.2)
                .setDepth(11_990)
                .setBlendMode(Phaser.BlendModes.ADD);
              this.chainSpectacleObjs.add(halo);
            }
            if (motionAllowed()) {
              // Scale-pop with follow-through (overshoot → settle-bounce → hold), matching the stamps.
              banner.setScale(0.72);
              this.tweens.chain({
                targets: banner,
                tweens: [
                  { scale: 1.08, duration: 260, ease: 'Back.easeOut' },
                  { scale: 0.98, duration: 120, ease: 'Sine.easeInOut' },
                  { scale: 1.0, duration: 110, ease: 'Sine.easeInOut' },
                ],
              });
            }
          }
          break;
        }
        default:
          break;
      }
    }
  }


  /**
   * GAUNTLET-CHAIN CRESCENDO — the signature WOW peak, now an ESCALATING three-tier build keyed on the
   * CONSECUTIVE-thread chain (chainStreak), NOT the raw per-board thread count. This is the S5 re-gate
   * (decision 1a): at armThreshold=1 nearly every correct is threaded, so a flat "fires on the 2nd
   * thread of the board" beat would be wallpaper — instead the crescendo now STAYS a peak by requiring
   * consecutive clean threads (a decoy-eat resets the chain) and by escalating strictly with the chain:
   *   • chain 2 → TIER 1: compact banner + riser tier 1 + one modest ring. No zoom, no slow-mo.
   *   • chain 3 → TIER 2: bigger banner + riser tier 2 + a brief zoom pulse + a music-intensity bump.
   *   • chain 4+ → TIER 3: the FULL crescendo — screen-level staging, riser tier 3, the announcer line,
   *                the anti-spammed slow-mo, a double shockwave, a firm shake, and the full music slam.
   * A single ordinary thread (chain 1) never reaches here — it uses the command-hint payoff only.
   *
   * `t` is the sim-event time (ms) used only as the anti-spam clock for the tier-3 slow-mo. Determinism:
   * a pure event reaction — never calls Core, never gates gameplay, and the slow-mo touches the visual/
   * tween clocks only (see hitstop()), so the sim tick schedule is untouched. Fully motion-gated: under
   * reduced-motion the zoom + slow-mo + pop are dropped and the spectacle collapses to static banner+SFX.
   */
export function fireGauntletChainCrescendo(this: GameScene,
    at: { x: number; y: number },
    chain: number,
    value: number | undefined,
    t: number,
    boardEndsThisBatch = false,
  ): void {
    const reduced = !motionAllowed();
    const tier = chain <= 2 ? 1 : chain === 3 ? 2 : 3;

    // Item-2 throttle: a TIER-3 re-fire (chain 5/6/7 landing in quick succession) must not STACK
    // rings/shakes/risers/slow-mos — that strobes the climax and machine-guns the riser. Reuse the
    // anti-spam clock: chain 4 (and any tier-3 ≥600ms later) fires the FULL beat; interim re-fires go
    // "quiet" and ONLY refresh the ×N stamp (which now replaces the prior one), so the number climbs
    // cleanly without re-detonating the spectacle. Tiers 1/2 always fire (brief + already escalating).
    const tier3Quiet = tier === 3 && this.lastChainSlowMoMs >= 0 && t - this.lastChainSlowMoMs < 600;
    if (tier === 3 && !tier3Quiet) this.lastChainSlowMoMs = t;

    // Clutter guard (video-gate WARN @00:03): on a fast chain a PRIOR reward float +
    // a skip-through float can still be rising when this stamp lands, stacking 3 labels. Wipe
    // in-flight float cards (same helper board-clear uses) so the GAUNTLET CHAIN ×N stamp reads ALONE.
    // The "+N" score rides on the stamp itself, so no score feedback is lost.
    clearActiveFloats(this);

    // Escalating riser SFX (tier 1/2/3) + the optional per-tier bark. Skipped on a throttled tier-3
    // re-fire so a long chain doesn't re-trigger the riser/VO every eat. The announcer's "GAUNTLET
    // CHAIN!" line is reserved for the TIER-3 peak (below), so the small tiers escalate as pure rising
    // SFX into the full announced climax.
    // S5R: suppress the riser + bark when the board ALSO ends this batch — the board-clear / PERFECT-
    // GAUNTLET set-piece owns the audio moment (the visual ×N stamp below still fires, so the number
    // still lands on screen; only the redundant stacked cue is dropped).
    if (!tier3Quiet && !boardEndsThisBatch) {
      audio().playSfx(tier === 1 ? 'chainRiser1' : tier === 2 ? 'chainRiser2' : 'chainRiser3');
      this.playOptionalCrescendoBark(tier);
    }

    const reach = Math.max(this.boardW, this.cellH * this.rows);

    if (tier === 1) {
      // TIER 1 (chain 2) — compact acknowledgement: one modest ring + a small spray + a faint edge.
      shockwaveRing(this, at.x, at.y, PALETTE.armed, { maxRadius: reach * 0.55, durationMs: 380, thickness: 4, peakAlpha: 0.42 });
      vignettePulse(this, PALETTE.armed, 300, 0.12);
      particleBurst(this, at.x, at.y, PALETTE.armed, 14);
      haptic(20);
    } else if (tier === 2) {
      // TIER 2 (chain 3) — bigger ring + a brief zoom pulse + a music-intensity BUMP (not the full slam).
      this.musicSwell = Math.max(this.musicSwell, 0.6);
      shockwaveRing(this, at.x, at.y, PALETTE.armed, { maxRadius: reach * 0.8, durationMs: 440, thickness: 5, peakAlpha: 0.52 });
      vignettePulse(this, PALETTE.armed, 340, 0.18);
      particleBurst(this, at.x, at.y, PALETTE.armed, 20);
      cameraPunch(this, 0.035, 180); // the brief zoom pulse (no-op under reduced-motion)
      haptic([20, 30]);
    } else if (!tier3Quiet) {
      // TIER 3 (chain 4+) — the FULL crescendo. Full music slam + the announcer line + the slow-mo peak.
      // Only fires on a non-throttled tier-3 (the anti-spam clock was stamped at the top of this call).
      this.musicSwell = 1;
      // #14: a TIER-3-EXCLUSIVE full-screen gold wash so the ×4+ climax is CATEGORICALLY bigger than the
      // per-cell tier-1/2 beats (the punch-list gap: ×4 read identical to ×2). A smooth EASED wash
      // (flash() is the VG-5 ramp — never a frame-1 slam) in the reward gold→amber family; flash() is
      // motion-gated internally (dampened + lengthened under reduceMotion) and the thin additive rings
      // below still carry the read there. Tiers 1/2 NEVER get the wash — it is the ×4+ signature.
      flash(this, PALETTE.armed, 340, 0.24);
      // S5R: on a board-ending batch the PERFECT-GAUNTLET set-piece sting owns the moment — skip the VO.
      if (!boardEndsThisBatch) audio().playVo('voGauntletChain');
      // Time-dilation slow-mo (visual clocks only — reuse hitstop()'s clock-scaling), opted into a raised
      // 300ms ceiling.
      if (!reduced) {
        const dilationMs = 220 + Math.min(chain - 4, 4) * 12; // chain 4→220ms … 8+→268ms
        hitstop(this, dilationMs, { ceilingMs: 300 });
      }
      // A punchier DOUBLE pulse — a bright thicker LEADING ring + a staggered trailing ECHO ("sonic
      // boom"). Thin additive STROKES only (no fill), which the gate confirmed do NOT wash answer glyphs.
      const ringReach = reach * (0.95 + Math.min(chain - 4, 4) * 0.04);
      const ringAlpha = Phaser.Math.Clamp(0.5 + (chain - 4) * 0.03, 0.5, 0.62);
      shockwaveRing(this, at.x, at.y, PALETTE.armed, { maxRadius: ringReach, durationMs: 460, thickness: 6, peakAlpha: ringAlpha });
      if (!reduced) {
        this.time.delayedCall(90, () => {
          if (!this.scene.isActive()) return;
          shockwaveRing(this, at.x, at.y, PALETTE.armed, { maxRadius: ringReach * 0.66, durationMs: 380, thickness: 3, peakAlpha: 0.4 });
        });
      }
      vignettePulse(this, PALETTE.armed, 360, 0.2);
      particleBurst(this, at.x, at.y, PALETTE.armed, Math.min(18 + chain * 4, 34));
      shake(this.cameras.main, Math.min(6 + (chain - 2), 8), 150);
      // Item-2 zoom clamp: keep the lens-punch GENTLE and bounded. A center-pivot zoom-in magnifies
      // distance-from-centre, so the old up-to-6% pushed the player's own tile (near a board edge at the
      // climax) off the top-left. A small kick reads as impact without eating the outer rows/columns.
      cameraPunch(this, Math.min(0.018 + chain * 0.003, 0.03), 170);
      haptic([25, 30, 50]);
    }

    // Hero stamp refreshes (destroying the prior one) so the latest ×N reads cleanly even on a
    // throttled tier-3 re-fire — a centered "GAUNTLET CHAIN ×N" banner sized by tier, motion-gated pop.
    // S9 FIX-D (G1 AMATEUR 2): in LANDSCAPE both this ×N stamp and the board-end stamp (CLINCHED! /
    // FLAWLESS) are frame-locked to the BOARD CENTRE (#18), so a board-ending chain munch superimposed
    // the two for the stamp's full life. Mirror the S5R AUDIO suppression for the visual: when this same
    // batch also ENDS the board, the board-end set-piece owns the centre — skip the transient ×N (the
    // +value still lands in the Result tally). Portrait keeps the stamp: its above-grid band and the
    // centred clear banner are disjoint zones, and portrait graded clean.
    if (!(boardEndsThisBatch && isLandscape())) this.showGauntletChainStamp(chain, value, tier);
  }


  /**
   * Forward VO seam (S5→S7): fire the optional per-tier rival bark for a chain crescendo. It's a silent
   * no-op until S7 registers the VO file (see audio.playOptionalBark), so the call-site cost is zero.
   */
export function playOptionalCrescendoBark(this: GameScene, tier: number): void {
    const key = tier === 1 ? 'barkChain2' : tier === 2 ? 'barkChain3' : 'barkChain4';
    // Shorter self-throttle (1.2s) than a normal bark so a fast 2→3→4 chain still escalates BYT3's panic
    // in voice instead of only the first tier speaking; tier-3 re-fires are already suppressed upstream.
    audio().playOptionalBark(key, { minGapMs: 1200 });
  }


  /**
   * "GAUNTLET CHAIN ×N" hero stamp. Portrait uses the same above-grid safe band as teach/clear
   * banners; landscape places the stamp in the right command rail so it never buries board labels.
   * Motion-gated pop; auto-dismisses ~900ms. Tracked in chainSpectacleObjs so SHUTDOWN can tear it down.
   */
export function showGauntletChainStamp(this: GameScene, n: number, value: number | undefined, tier = 3): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const land = isLandscape();
    const reduced = !motionAllowed();
    // Item-2 fix: destroy the PREVIOUS chain stamp before drawing the next. On a fast 5/6/7 chain the old
    // stamp was still mid-fade when the new one landed, stacking two text objects → the "×4 5 +225"
    // ghosted double-strike + a stray prior digit. clearActiveFloats only wipes the float CARDS, not this
    // stamp, so we track + tear down the one live stamp ourselves. Only the latest ×N ever reads.
    if (this.chainStampObj) {
      this.tweens.killTweensOf(this.chainStampObj);
      this.chainSpectacleObjs.delete(this.chainStampObj);
      this.chainStampObj.destroy();
      this.chainStampObj = undefined;
    }
    // Banner grows strictly with the tier so the escalation reads on-screen (compact → bigger → hero).
    // #14: tier-3 (×4+) takes a CATEGORICAL size jump (max 27 → 44) so the climax is unmistakably bigger
    // than the tier-2 per-cell beat, matching its exclusive full-screen wash / slow-mo / announcer peak.
    const sizeOpts = land
      ? tier === 1
        ? { vw: 0.024, min: 14, max: 18 }
        : tier === 2
          ? { vw: 0.029, min: 16, max: 22 }
          : { vw: 0.04, min: 22, max: 31 }
      : tier === 1
        ? { vw: 0.034, min: 14, max: 17 }
        : tier === 2
          ? { vw: 0.04, min: 16, max: 20 }
          : { vw: 0.058, min: 24, max: 32 };
    // #12 HERO WORDMARK: the shared bespoke display cut (gold→amber gradient + tight tracking), with the
    // ×N broken out to its OWN token at ~1.6× and a forward lean — the arcade combo read. The honest
    // per-thread +value rides UNDER it as a smaller sub-line (never inflated — exactly this eat's scored
    // gauntlet bonus, the value the float shows). One container so it pops/fades/tears down as a unit.
    const mainPx = typePx(this, 'display', sizeOpts);
    // S9 FIX-D (G1 AMATEUR 1, root-caused): the stroke was sized off SCREEN width (W*0.012 → 15.4px at
    // W=1280) while the GLYPHS size off the tier (tier-1/2 land 17–27px on desktop) — a stroke over half
    // the glyph height fuses letterforms into "GALNTLETCHAN" for the stamp's whole ~1.3s life. No stroke
    // override: styleHeroText's default is height-PROPORTIONAL (max(4, glyphHeight*0.09)), so every tier
    // and viewport gets an outline scaled to its own letterforms — the ×N token (1.6× glyph) likewise
    // derives its own heavier stroke from its own height. Tier-3/mobile keep a near-identical weight.
    const { container: wordmark } = heroWordmark(this, {
      main: 'GAUNTLET CHAIN',
      mult: mult(n), // #46: standardized ×N glyph (never a lowercase 'x')
      sub: value !== undefined ? `+${value}` : undefined,
      size: mainPx,
      depth: 12_500,
    });
    let stamp = wordmark;
    if (!land) {
      const wordBounds = wordmark.getBounds();
      const chipW = Math.max(204, Math.min(W * 0.62, wordBounds.width + 36));
      const chipH = Math.max(44, wordBounds.height + 16);
      const bg = this.add.graphics().setDepth(12_499);
      bg.fillStyle(PALETTE.void, 0.88);
      bg.fillRoundedRect(-chipW / 2, -chipH / 2, chipW, chipH, 14);
      bg.lineStyle(2, PALETTE.gold, 0.95);
      bg.strokeRoundedRect(-chipW / 2, -chipH / 2, chipW, chipH, 14);
      bg.lineStyle(1, PALETTE.armed, 0.45);
      bg.strokeRoundedRect(-chipW / 2 + 4, -chipH / 2 + 4, chipW - 8, chipH - 8, 10);
      stamp = this.add.container(0, 0, [bg, wordmark]).setDepth(12_500);
    }
    const contentH = stamp.getBounds().height;
    const halfH = contentH / 2;
    // PLACEMENT — the stamp is centred on its own origin, so it sits directly at the centre `y`.
    const boardTop = this.gridY;
    let x = W / 2;
    let y: number;
    if (land) {
      // Landscape has no clean band over/under the near-full-height board. Put the transient combo
      // stamp into the right command rail, between timer and dock controls, instead of covering answers.
      const boardH = this.cellH * this.rows;
      const boardRight = this.gridX + this.cellW * this.cols;
      const railCenterX = W - LANDSCAPE_GUTTER_W / 2;
      x = Phaser.Math.Clamp(railCenterX, boardRight + 78, W - 70);
      const topSafe = boardTop + Math.max(82, halfH + 16);
      const bottomSafe = Math.min(H - Math.max(216, halfH + 32), boardTop + boardH - halfH - 14);
      const idealY = boardTop + boardH * 0.42;
      y = topSafe <= bottomSafe ? Phaser.Math.Clamp(idealY, topSafe, bottomSafe) : idealY;
    } else {
      // Portrait docks the chain reward as an explicit chip just under the QUOTA pill. It still clamps
      // above row 1; a short tether below the chip connects the reward lane to the board.
      const quotaAnchorY = this.winStatusText?.active ? this.winStatusText.y : boardTop - halfH - 52;
      const idealY = quotaAnchorY + halfH + 10;
      const maxY = boardTop - halfH - 8; // lowest centre keeping the bottom edge above the grid top
      const minY = Math.max(10 + halfH, quotaAnchorY + halfH + 10);
      if (minY <= maxY) {
        y = Phaser.Math.Clamp(idealY, minY, maxY);
      } else {
        // Short portrait stages cannot fit the chip between QUOTA and row 1. Move it above QUOTA
        // rather than forcing a chip/pill overlap in the only legible scoring band.
        y = Math.max(10 + halfH, quotaAnchorY - halfH - 10);
      }
    }
    stamp.setPosition(x, y);
    if (!land) {
      const tetherTop = halfH + 5;
      const tetherBottom = boardTop - 10 - y;
      const stampBelowQuota = !this.winStatusText?.active || y > this.winStatusText.y;
      if (stampBelowQuota && tetherBottom > tetherTop + 14) {
        const tether = this.add.graphics();
        tether.lineStyle(2, PALETTE.gold, 0.42);
        tether.lineBetween(0, tetherTop, 0, tetherBottom);
        tether.lineStyle(1, PALETTE.armed, 0.34);
        tether.lineBetween(-5, tetherTop + 5, -5, tetherBottom - 2);
        tether.lineBetween(5, tetherTop + 5, 5, tetherBottom - 2);
        stamp.addAt(tether, 0);
      }
    }
    this.chainSpectacleObjs.add(stamp);
    this.chainStampObj = stamp;
    const cleanup = (): void => {
      this.chainSpectacleObjs.delete(stamp);
      if (this.chainStampObj === stamp) this.chainStampObj = undefined;
      stamp.destroy();
    };
    if (reduced) {
      // Static: no pop. Hold briefly then fade.
      this.time.delayedCall(700, () => {
        if (!stamp.active) return;
        this.tweens.add({ targets: stamp, alpha: 0, duration: 200, onComplete: cleanup });
      });
      return;
    }
    stamp.setScale(0.7);
    this.tweens.add({ targets: stamp, scale: 1.04, duration: 160, ease: 'Back.easeOut', yoyo: true });
    this.time.delayedCall(900, () => {
      if (!stamp.active) {
        this.chainSpectacleObjs.delete(stamp);
        if (this.chainStampObj === stamp) this.chainStampObj = undefined;
        return;
      }
      this.tweens.add({
        targets: stamp,
        alpha: 0,
        scale: 1.12,
        duration: 240,
        ease: 'Quad.easeIn',
        onComplete: cleanup,
      });
    });
  }


  /**
   * PERFECT FEAST hero stamp — the flawless gauntlet-board clear banner (distinct from the routine
   * "BOARD CLEAR"). Gold "FLAWLESS" with a celebratory motion-gated pop, centered, auto-dismissing.
   * Replaces the standard clear banner so the clear reads with a single hero line. Tracked for SHUTDOWN.
   */
export function showFlawlessFeast(this: GameScene): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const reduced = !motionAllowed();
    const stamp = this.add
      .text(W / 2, H / 2, 'FLAWLESS', {
        fontFamily: FONT.display,
        fontSize: `${typePx(this, 'display', { vw: 0.078, min: 34, max: 60 })}px`,
        color: hex(0xffd54a),
        align: 'center',
        fontStyle: 'bold',
        stroke: '#0a0b12',
        strokeThickness: 7,
      })
      .setOrigin(0.5)
      .setDepth(12_500);
    // #12 shared hero cut — gold→amber gradient + tight tracking (upright: FLAWLESS is a monument beat).
    styleHeroText(stamp, { strokeThickness: 7 });
    this.chainSpectacleObjs.add(stamp);
    const cleanup = (): void => {
      this.chainSpectacleObjs.delete(stamp);
      stamp.destroy();
    };
    if (reduced) {
      this.time.delayedCall(1100, () => {
        if (!stamp.active) return;
        this.tweens.add({ targets: stamp, alpha: 0, duration: 220, onComplete: cleanup });
      });
      return;
    }
    // Scale-pop with follow-through: overshoot in, a small settle-bounce, then hold at 1.0 (motion-gated).
    stamp.setScale(0.5);
    this.tweens.chain({
      targets: stamp,
      tweens: [
        { scale: 1.12, duration: 240, ease: 'Back.easeOut' },
        { scale: 0.98, duration: 130, ease: 'Sine.easeInOut' },
        { scale: 1.0, duration: 120, ease: 'Sine.easeInOut' },
      ],
    });
    this.time.delayedCall(1200, () => {
      if (!stamp.active) {
        this.chainSpectacleObjs.delete(stamp);
        return;
      }
      this.tweens.add({ targets: stamp, alpha: 0, duration: 260, ease: 'Quad.easeIn', onComplete: cleanup });
    });
  }


  /**
   * #34 FIRST BITE — the one-shot celebratory hero float on the human's first correct CHOMP of the
   * untimed first training board. Uses the shared #12 hero wordmark cut (gold→amber gradient), placed
   * in the same safe above-grid band as the chain/teach stamps so it never buries a live answer tile,
   * and torn down via chainSpectacleObjs on SHUTDOWN. Fully motion-gated (static hold+fade under
   * reduced-motion). Encouragement only — there is no fail pressure on board 0 (no timer, no hazards).
   */
export function showFirstBiteStamp(this: GameScene): void {
    const W = this.scale.width;
    const reduced = !motionAllowed();
    const mainPx = typePx(this, 'display', { vw: 0.07, min: 26, max: 40 });
    const { container: stamp } = heroWordmark(this, {
      main: 'FIRST BITE!',
      sub: 'nice — keep eating the right answers',
      size: mainPx,
      depth: 12_500,
      cut: { strokeThickness: Math.max(5, W * 0.012) },
    });
    const halfH = stamp.getBounds().height / 2;
    const boardTop = this.gridY;
    let y: number;
    if (isLandscape()) {
      // Landscape: anchor on the board CENTRE (the rule marquee caps the band above the grid) — matches
      // the chain/set-piece stamp placement so it can never bisect the rule text.
      y = boardTop + (this.cellH * this.rows) / 2;
    } else {
      // Portrait: bottom edge a GAP above the grid top, clamped fully on-screen (mirrors the chain stamp).
      const GAP = 18;
      const topPad = 10;
      const idealY = boardTop - halfH - GAP;
      const maxY = boardTop - halfH - 8;
      const minY = topPad + halfH;
      y = minY <= maxY ? Phaser.Math.Clamp(idealY, minY, maxY) : Math.max(topPad + halfH, idealY);
    }
    stamp.setPosition(W / 2, y);
    this.chainSpectacleObjs.add(stamp);
    const cleanup = (): void => {
      this.chainSpectacleObjs.delete(stamp);
      stamp.destroy();
    };
    if (reduced) {
      this.time.delayedCall(1150, () => {
        if (!stamp.active) return;
        this.tweens.add({ targets: stamp, alpha: 0, duration: 220, onComplete: cleanup });
      });
      return;
    }
    stamp.setScale(0.6);
    this.tweens.chain({
      targets: stamp,
      tweens: [
        { scale: 1.08, duration: 240, ease: 'Back.easeOut' },
        { scale: 0.98, duration: 120, ease: 'Sine.easeInOut' },
        { scale: 1.0, duration: 110, ease: 'Sine.easeInOut' },
      ],
    });
    this.time.delayedCall(1250, () => {
      if (!stamp.active) {
        this.chainSpectacleObjs.delete(stamp);
        return;
      }
      this.tweens.add({ targets: stamp, alpha: 0, scale: 1.1, duration: 240, ease: 'Quad.easeIn', onComplete: cleanup });
    });
  }


  /** A brief teaching callout that auto-dismisses. Used for the one-time "LOCKED" beat. */
export function showTeachBeat(this: GameScene, message: string): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const boardTop = this.gridY;
    const boardBottom = this.gridY + this.cellH * this.rows;
    const bottomHudTop = H - Math.min(54, H * 0.08);
    const pad = Math.max(9, Math.min(12, W * 0.03));
    const txt = this.add
      .text(0, 0, message, {
        fontFamily: FONT.display,
        // Bumped for mobile readability (the video gate repeatedly flagged this teach beat as small);
        // the card auto-sizes to the text and the row-1-clearance clamp below absorbs the taller card.
        fontSize: `${typePx(this, 'body', { vw: 0.04, min: 15 })}px`,
        color: hex(PALETTE.white),
        align: 'center',
        fontStyle: 'bold',
        stroke: '#0a0b12',
        strokeThickness: 2,
        wordWrap: { width: W * 0.82 },
      })
      .setOrigin(0.5);
    const bg = this.add
      .rectangle(0, 0, Math.min(W * 0.9, txt.width + pad * 2), txt.height + pad * 2, PALETTE.banner, 0.96)
      .setStrokeStyle(2, PALETTE.lockBorder, 1);
    const cardH = bg.height;
    // Sit the card cleanly ABOVE the grid in the rule-banner zone with a clear gap, so it never overlaps
    // board row-1 (the SAFE-PASS / teach beat used to hug the grid with only 8px and could touch a cell
    // at 360px). GAP gives breathing room; the card's BOTTOM edge is clamped to stay above boardTop, and
    // we only fall to the bottom slot (above the HUD) when the top zone genuinely can't fit the card.
    const GAP = 14;
    const topSlotY = boardTop - cardH / 2 - GAP;
    const bottomSlotY = boardBottom + cardH / 2 + GAP;
    // top zone fits if the card's whole height clears the banner/HUD band above the grid.
    const topFits = topSlotY - cardH / 2 >= 64;
    const y = topFits
      ? Math.min(topSlotY, boardTop - cardH / 2 - GAP) // never let the card bottom cross into row-1
      : Math.min(bottomSlotY, bottomHudTop - cardH / 2 - 4);
    const card = this.add.container(W / 2, y, [bg, txt]).setDepth(13_000).setAlpha(0);
    this.tweens.add({ targets: card, alpha: 1, duration: 160, ease: 'Quad.easeOut' });
    this.time.delayedCall(1350, () => {
      if (!card.active) return;
      this.tweens.add({
        targets: card,
        alpha: 0,
        duration: 280,
        ease: 'Quad.easeIn',
        onComplete: () => card.destroy(),
      });
    });
  }


  // =============================================================================
  // FACE & FURY S5 — board-end SET-PIECES (PERFECT GAUNTLET + PHOTO FINISH)
  // Renderer-derived, fired between board end and the Result handoff. The sim is already DECIDED when
  // these run (snap.status !== 'playing'), so a longer time-dilation is safe — it can never change the
  // outcome. Everything is presentation only: no Core call, no scoring, no determinism touch.
  // =============================================================================

  /** Tear down any in-flight chain-spectacle objects so a board-end set-piece stamp reads ALONE. */
export function clearChainSpectacle(this: GameScene): void {
    for (const o of this.chainSpectacleObjs) {
      try {
        this.tweens.killTweensOf(o);
        o.destroy();
      } catch {
        /* already gone */
      }
    }
    this.chainSpectacleObjs.clear();
    this.chainStampObj = undefined; // the live ×N stamp (if any) was in the set above — drop the dangling ref
  }

  /**
   * PERFECT GAUNTLET predicate (renderer-derived from the event log + final snapshot): the board was
   * WON by the human, the human ate ZERO decoys, ate at least 3 corrects, AND every correct they ate
   * was THREADED (each has a matching player-0 `gauntlet` event). A STRICT superset of Flawless Feast —
   * this is the "flawless clean route" clip moment, not merely a zero-decoy clear. Never touches Core.
   */
export function detectPerfectGauntlet(this: GameScene, snap: Snapshot): boolean {
    if (snap.status !== 'won') return false;
    const human = snap.players.find((p) => p.id === PLAYER);
    if (!human || human.eatenDecoy !== 0) return false;
    // Count the human's threaded eats = its `gauntlet` events over the whole board log.
    let threaded = 0;
    for (const ev of this.sim.log.all()) {
      if (ev.type === 'gauntlet' && ev.player === PLAYER) threaded += 1;
    }
    return threaded >= 3 && threaded === human.eatenCorrect;
  }

  /**
   * PERFECT GAUNTLET set-piece — the signature clip. A screen-consuming payoff that OWNS the clear:
   * a dramatic dimming overlay (time-dilation feel; safe — the sim is decided), a slow camera push-in,
   * a big gold "PERFECT / GAUNTLET" wordmark staged in, gold confetti, and the perfect-gauntlet sting.
   * The longer Result handoff (tickFrame reads this.perfectGauntlet) lets it read before the scene swap.
   * reduceMotion: no slow-mo / zoom / scale — a dignified STATIC card + the sting still fire.
   */
export function firePerfectGauntletSetPiece(this: GameScene): void {
    const reduced = !motionAllowed();
    // Any in-flight chain stamp (the winning thread almost always fires one) is cleared so the wordmark
    // reads alone, and any lingering hitstop from that crescendo is released (the sim is decided).
    this.clearChainSpectacle();
    clearActiveFloats(this);
    this.tweens.timeScale = 1;
    this.time.timeScale = 1;

    audio().playSfx('perfectGauntlet');
    // S7b: the triumphant swell rides UNDER the sting (the sting is the transient attack, this is the
    // sustained musical bloom filling the wordmark hold). Same hard 2s duck, so the beds + villain layer
    // drop and the barkPerfect VO ("Not one decoy?! ...How?") rides ABOVE both on the SFX bus.
    audio().playSfx('perfectTriumph');
    this.playOptionalSetPieceBark('barkPerfect');

    const W = this.scale.width;
    const H = this.scale.height;
    // Screen-consuming dark overlay (the world holds its breath). Fades in; held until the scene swap
    // (tracked so SHUTDOWN tears it down). Under reduced-motion it sits at a calm static alpha.
    // ROOT-CAUSE (S5 round-2): a Phaser Shape renders its FILL at `fillAlpha × alpha` (see
    // RectangleWebGLRenderer). Building it with fillAlpha=0 and then driving the GameObject `alpha`
    // (AlphaSingle) never touches fillAlpha, so 0 × alpha = 0 → the dim was ALWAYS transparent (even the
    // synchronous reduced-motion setAlpha). The fill MUST be opaque (fillAlpha=1) and the visible density
    // driven purely by the GameObject alpha, which the tween/setAlpha below already animate.
    const overlay = this.add
      .rectangle(W / 2, H / 2, W, H, 0x05070f, 1)
      .setScrollFactor(0)
      .setAlpha(0)
      .setDepth(12_400);
    this.chainSpectacleObjs.add(overlay);
    if (reduced) {
      overlay.setAlpha(0.42);
    } else {
      this.tweens.add({ targets: overlay, alpha: 0.52, duration: 260, ease: 'Quad.easeOut' });
      // Slow cinematic camera push-in then settle (bullet-time). No-op if the camera is already tweening.
      const cam = this.cameras.main;
      if (!this.tweens.isTweening(cam)) {
        const base = cam.zoom;
        this.tweens.add({ targets: cam, zoom: base * 1.06, duration: 520, ease: 'Sine.easeOut', yoyo: true, hold: 760, onComplete: () => cam.setZoom(base) });
      }
    }

    // Gold celebration behind the wordmark (routes to the calm legacy path under reduced-motion).
    celebrate(this, [PALETTE.gold, PALETTE.armed, PALETTE.correct, PALETTE.white], { count: reduced ? 12 : 110, depth: 12_450 });
    this.showPerfectGauntletWordmark();
    shake(this.cameras.main, 6, 220);
    haptic([40, 60, 40, 80]);
  }

  /** The stacked gold "PERFECT / GAUNTLET" hero wordmark + subtitle. Tracked for SHUTDOWN teardown. */
export function showPerfectGauntletWordmark(this: GameScene): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const reduced = !motionAllowed();
    const bigPx = typePx(this, 'display', { vw: 0.13, min: 44, max: 88 });
    const mk = (str: string, y: number, size: number, color: number): Phaser.GameObjects.Text => {
      const t = this.add
        .text(W / 2, y, str, {
          fontFamily: FONT.display,
          fontSize: `${size}px`,
          color: hex(color),
          align: 'center',
          fontStyle: 'bold',
          stroke: '#0a0b12',
          strokeThickness: Math.max(6, W * 0.014),
        })
        .setOrigin(0.5)
        .setDepth(12_600);
      t.setShadow(0, 4, '#000000', 12, false, true);
      this.chainSpectacleObjs.add(t);
      return t;
    };
    const top = mk('PERFECT', H * 0.42, bigPx, PALETTE.gold);
    const bottom = mk('GAUNTLET', H * 0.42 + bigPx * 0.98, bigPx, PALETTE.gold);
    const sub = mk('EVERY ANSWER THREADED', H * 0.42 + bigPx * 1.85, typePx(this, 'body', { vw: 0.036, min: 14, max: 22 }), PALETTE.armed);
    // #12 shared hero cut on the two hero lines (gold→amber gradient + tight tracking). Upright — the
    // PERFECT GAUNTLET set-piece is the game's monument moment; the subtitle keeps its flat armed amber.
    styleHeroText(top, { strokeThickness: Math.max(6, W * 0.014) });
    styleHeroText(bottom, { strokeThickness: Math.max(6, W * 0.014) });
    // #3: a persistent dark CONTRAST PLATE hugging the wordmark so PERFECT / GAUNTLET separates cleanly
    // from any live tile labels underneath — INDEPENDENT of the dim overlay. Under reduceMotion the
    // set-piece drops the slow-mo/zoom and leans on a single static overlay; the plate is the guaranteed
    // backing that "survives reduceMotion" (static, always drawn, sized to the resting glyph scale).
    // Gold-edged to tie it to the wordmark; sits above the overlay (12_400) + confetti (12_450), below
    // the hero text (12_600); tracked for SHUTDOWN teardown.
    const pb = [top, bottom, sub].map((t) => t.getBounds());
    const pLeft = Math.min(...pb.map((b) => b.left));
    const pRight = Math.max(...pb.map((b) => b.right));
    const pTop = Math.min(...pb.map((b) => b.top));
    const pBot = Math.max(...pb.map((b) => b.bottom));
    const platePadX = Math.max(24, W * 0.06);
    const platePadY = Math.max(16, H * 0.022);
    const plate = this.add.graphics().setScrollFactor(0).setDepth(12_540);
    plate.fillStyle(0x05060c, 0.62);
    plate.fillRoundedRect(pLeft - platePadX, pTop - platePadY, pRight - pLeft + platePadX * 2, pBot - pTop + platePadY * 2, 22);
    plate.lineStyle(1.5, PALETTE.gold, 0.5);
    plate.strokeRoundedRect(pLeft - platePadX, pTop - platePadY, pRight - pLeft + platePadX * 2, pBot - pTop + platePadY * 2, 22);
    this.chainSpectacleObjs.add(plate);
    if (reduced) return; // static hold — the scene swap destroys them
    top.setScale(0.55);
    bottom.setScale(0.5);
    sub.setAlpha(0);
    this.tweens.add({ targets: top, scale: 1, duration: 300, ease: 'Back.easeOut' });
    this.tweens.add({ targets: bottom, scale: 1, duration: 340, delay: 120, ease: 'Back.easeOut' });
    this.tweens.add({ targets: sub, alpha: 1, duration: 300, delay: 400, ease: 'Quad.easeOut' });
  }

  /**
   * PHOTO-FINISH detection (renderer-derived from the final scores). A board-deciding finish where the
   * human's final score is within PHOTO_FINISH_MARGIN of the opponent's — the rival in an AI match, or
   * the ghost's live score in a duel (both compared on the SAME munch-sum basis so a perfect-board/surge
   * bonus can't fake a tie). Returns { close, margin }; close=false when there's no scored opponent.
   *
   * OPPONENT PRIORITY (item 4): when a GHOST race is live (a self-race OR a ladder duel — and a ladder
   * duel FORCES the rival on, so `aiEnabled` is ALSO true in that case) the stake the player is actually
   * watching is the GHOST margin, not BYT3's. So prefer the ghost munch-sum margin WHENEVER a ghost racer
   * exists, and fall back to the AI score margin only when no ghost is racing.
   */
export function detectPhotoFinish(this: GameScene, snap: Snapshot): { close: boolean; margin: number } {
    if (this.ghostRacer) {
      // Ghost race live → the ghost margin is the narrative stake (outranks the AI even in a ladder duel
      // where both are on). Compare the human's MUNCH-SUM (the ghost's basis) vs the ghost's live chip.
      const humanTrack = buildGhostTrack(this.sim.log.all()).finalScore;
      const margin = Math.abs(humanTrack - this.ghostRacer.liveScore);
      return { close: margin <= PHOTO_FINISH_MARGIN, margin };
    }
    if (this.aiEnabled) {
      const human = snap.players.find((p) => p.id === PLAYER);
      const ai = snap.players.find((p) => p.id === AI);
      if (human && ai) {
        const margin = Math.abs(human.score - ai.score);
        return { close: margin <= PHOTO_FINISH_MARGIN, margin };
      }
    }
    return { close: false, margin: Infinity };
  }

  /**
   * PHOTO-FINISH beat — a bullet-time transition on a nail-biter finish: a brief time-dilation (safe —
   * the sim is decided), then a camera-punch "snap" + a static PHOTO FINISH tag as the dilation releases.
   * The slow-mo-whoosh-into-snap SFX carries the drama. reduceMotion: skip the dilation + punch, keep
   * the snap tag + SFX. The longer Result handoff (tickFrame reads the close-finish result) lets it read.
   */
export function firePhotoFinishBeat(this: GameScene, won: boolean, margin: number): void {
    const reduced = !motionAllowed();
    this.clearChainSpectacle();
    clearActiveFloats(this);
    this.tweens.timeScale = 1;
    this.time.timeScale = 1;

    audio().playSfx('photoFinish');
    this.playOptionalSetPieceBark('barkPhotoFinish');
    haptic([30, 60]);

    if (reduced) {
      // No dilation, no desat/streaks — the static snap tag lands immediately alongside the SFX (item 3
      // reduced-motion contract: keep the tag + SFX, skip every motion/colour treatment).
      this.showPhotoFinishTag(won, margin);
      return;
    }
    // Item 3 — make the STILL read as bullet-time (a photo-finish frozen frame): a cool colour-drain
    // BLEACH that flattens the board toward a frozen slate + a dark VIGNETTE + radial SPEED-STREAKS
    // during the dilation, so a paused capture instantly reads "time froze at the wire", not just a
    // normal transition. All presentation; the sim is already decided. (Phaser 3.90 cameras have no
    // .postFX ColorMatrix, so the desaturated read is a SCREEN bleach wash rather than a grade.) Every
    // piece is tracked in chainSpectacleObjs, so SHUTDOWN tears it all down.
    this.showPhotoFinishBleach();
    this.showPhotoFinishVignette();
    this.showPhotoFinishStreaks();
    // Slow-mo whoosh (visual clocks only), then the hard SNAP (camera punch + shake + tag) as it releases.
    hitstop(this, 360, { ceilingMs: 420 });
    window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      cameraPunch(this, 0.05, 200);
      shake(this.cameras.main, 6, 140);
      this.showPhotoFinishTag(won, margin);
    }, 380);
  }

  /**
   * Item 3 — a cool SCREEN "bleach" wash that flattens the board toward a frozen slate, standing in for a
   * true desaturation grade (Phaser 3.90 cameras have no .postFX ColorMatrix). SCREEN lifts the dark OLED
   * field toward a cold grey so the frozen frame reads drained-of-colour under the tag. fillAlpha carries
   * the density (a fillAlpha=0 rect never composites — S5 round-2 root cause), GameObject alpha ramps it.
   */
export function showPhotoFinishBleach(this: GameScene): void {
    const W = this.scale.width;
    const H = this.scale.height;
    // Set alpha DIRECTLY (not via a tween): firePhotoFinishBeat runs a hitstop that freezes the tween
    // clock (timeScale≈0), so a fade tween would stay invisible until the freeze releases ~360ms later —
    // the frozen frame must SNAP in with the dilation. SHUTDOWN tears it down (it's in chainSpectacleObjs).
    const wash = this.add
      .rectangle(W / 2, H / 2, W, H, 0x5a6478, 0.34)
      .setScrollFactor(0)
      .setBlendMode(Phaser.BlendModes.SCREEN)
      .setDepth(12_340);
    this.chainSpectacleObjs.add(wash);
  }

  /** Item 3 — a dark closing-frame vignette for the photo-finish bullet-time beat. SHUTDOWN tears it down. */
export function showPhotoFinishVignette(this: GameScene): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const thick = Math.max(28, Math.min(W, H) * 0.11);
    // Alpha set directly (the hitstop freezes the tween clock — see showPhotoFinishBleach). Full on frame 1.
    const g = this.add.graphics().setScrollFactor(0).setDepth(12_350);
    g.fillStyle(0x05060c, 0.85);
    g.fillRect(0, 0, W, thick);
    g.fillRect(0, H - thick, W, thick);
    g.fillRect(0, 0, thick, H);
    g.fillRect(W - thick, 0, thick, H);
    this.chainSpectacleObjs.add(g);
  }

  /** Item 3 — radial SPEED-STREAKS shooting outward from centre (sells the bullet-time freeze). */
export function showPhotoFinishStreaks(this: GameScene): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const cx = W / 2;
    const cy = H / 2;
    // Alpha set directly (the hitstop freezes the tween clock — see showPhotoFinishBleach): the speed
    // lines are FROZEN into the bullet-time still, then torn down at the scene swap (SHUTDOWN). Low alpha
    // + behind the tag (depth 12_360 < scrim 12_540 < tag 12_600) so they never fight the headline.
    const g = this.add.graphics().setScrollFactor(0).setDepth(12_360).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.42);
    const outer = Math.max(W, H) * 0.85;
    const inner = Math.max(W, H) * 0.34;
    const N = 22;
    g.lineStyle(2, 0xffffff, 0.6);
    for (let i = 0; i < N; i += 1) {
      const a = (i / N) * Math.PI * 2 + (i % 2) * 0.14;
      g.beginPath();
      g.moveTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
      g.lineTo(cx + Math.cos(a) * outer, cy + Math.sin(a) * outer);
      g.strokePath();
    }
    this.chainSpectacleObjs.add(g);
  }

  /** The centered "PHOTO FINISH" tag + who edged it. Tracked for SHUTDOWN teardown; scene swap destroys. */
export function showPhotoFinishTag(this: GameScene, won: boolean, margin: number): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const reduced = !motionAllowed();
    const color = won ? PALETTE.correct : PALETTE.hazard;
    // Item 3 — a dark SCRIM plate behind the tag so "PHOTO FINISH / YOU EDGED IT" separates cleanly from
    // the (now desaturated) tile labels underneath instead of colliding with them. Sized to span the
    // two-line block, centred on the tag; tracked for SHUTDOWN teardown.
    const scrimW = Math.min(W * 0.9, W * 0.62 + 220);
    const scrimTop = H * 0.46 - typePx(this, 'display', { vw: 0.086, min: 30, max: 54 }) * 0.72;
    const scrimH = typePx(this, 'display', { vw: 0.086, min: 30, max: 54 }) * 1.7 + typePx(this, 'body', { vw: 0.032, min: 13, max: 20 }) * 1.2;
    const scrim = this.add.graphics().setScrollFactor(0).setDepth(12_540);
    scrim.fillStyle(0x05060c, 0.72);
    scrim.fillRoundedRect(W / 2 - scrimW / 2, scrimTop, scrimW, scrimH, 16);
    scrim.lineStyle(1.5, color, 0.6);
    scrim.strokeRoundedRect(W / 2 - scrimW / 2, scrimTop, scrimW, scrimH, 16);
    this.chainSpectacleObjs.add(scrim);
    const main = this.add
      .text(W / 2, H * 0.46, 'PHOTO FINISH', {
        fontFamily: FONT.display,
        fontSize: `${typePx(this, 'display', { vw: 0.086, min: 30, max: 54 })}px`,
        color: hex(PALETTE.white),
        align: 'center',
        fontStyle: 'bold',
        stroke: '#0a0b12',
        strokeThickness: Math.max(6, W * 0.013),
      })
      .setOrigin(0.5)
      .setDepth(12_600);
    main.setShadow(0, 4, '#000000', 10, false, true);
    // #12 shared hero cut with a forward lean — a photo finish is THE nail-biter clip; gold→amber reads
    // as spectacle regardless of who edged it (the win/loss is carried by the sub-line colour below).
    styleHeroText(main, { skew: true, strokeThickness: Math.max(6, W * 0.013) });
    const sub = this.add
      .text(W / 2, H * 0.46 + typePx(this, 'display', { vw: 0.086, min: 30, max: 54 }) * 0.9, `${won ? 'YOU EDGED IT' : 'EDGED OUT'} · ${margin} pts`, {
        fontFamily: FONT.display,
        fontSize: `${typePx(this, 'body', { vw: 0.032, min: 13, max: 20 })}px`,
        color: hex(color),
        align: 'center',
        fontStyle: 'bold',
        stroke: '#0a0b12',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(12_600);
    this.chainSpectacleObjs.add(main);
    this.chainSpectacleObjs.add(sub);
    if (reduced) return;
    main.setScale(0.6);
    sub.setAlpha(0);
    this.tweens.add({ targets: main, scale: 1, duration: 220, ease: 'Back.easeOut' });
    this.tweens.add({ targets: sub, alpha: 1, duration: 220, delay: 140, ease: 'Quad.easeOut' });
  }

  /** Forward VO seam (S5→S7): the optional set-piece rival bark (barkPerfect / barkPhotoFinish). */
export function playOptionalSetPieceBark(this: GameScene, key: 'barkPerfect' | 'barkPhotoFinish'): void {
    // setPiece: the HERO punchline bypasses the self-throttle so it lands even when a chain bark spoke
    // seconds earlier (the winning thread's chain bark is already de-stacked on a board-ending batch).
    audio().playOptionalBark(key, { setPiece: true });
  }
